import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Check } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/Breadcrumbs';

interface LocationInfo {
  name: string;
  intro: string;
  population: string;
  monthlySearches: string;
  trades: string[];
  nearbyAreas: string[];
  coordinates: {
    lat: number;
    lng: number;
  };
}

const locationData: Record<string, LocationInfo> = {
  // Swansea Bay Region
  'llanelli': {
    name: 'Llanelli',
    intro: 'As the largest town in Carmarthenshire, Llanelli has a thriving trades sector. From the coastal developments at North Dock to established residential areas, local tradespeople serve both commercial and domestic clients across the area.',
    population: '49,591',
    monthlySearches: '800+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'Roofers'],
    nearbyAreas: ['Burry Port', 'Swiss Valley', 'Bynea', 'Gorseinon'],
    coordinates: { lat: 51.6802, lng: -4.1629 },
  },
  'swansea': {
    name: 'Swansea',
    intro: "Wales' second-largest city offers tremendous opportunities for trades. With constant waterfront development and urban regeneration projects, having a strong online presence is essential to stand out in this competitive market.",
    population: '246,563',
    monthlySearches: '2,400+',
    trades: ['All Trades', 'Commercial Contractors', 'Specialists'],
    nearbyAreas: ['Mumbles', 'Sketty', 'Morriston', 'Gorseinon'],
    coordinates: { lat: 51.6214, lng: -3.9436 },
  },
  'neath': {
    name: 'Neath',
    intro: 'A historic market town at the gateway to the Neath and Dulais valleys. Home to a strong community of trades serving both the town centre and surrounding villages, with excellent transport links via the M4.',
    population: '50,658',
    monthlySearches: '650+',
    trades: ['Plumbers', 'Builders', 'Electricians', 'Heating Engineers'],
    nearbyAreas: ['Port Talbot', 'Swansea', 'Resolven', 'Seven Sisters'],
    coordinates: { lat: 51.6604, lng: -3.8035 },
  },
  'port-talbot': {
    name: 'Port Talbot',
    intro: 'An industrial town with strong community ties, Port Talbot offers opportunities for trades serving both residential and commercial clients. The town is undergoing regeneration, creating demand for skilled tradespeople.',
    population: '35,633',
    monthlySearches: '500+',
    trades: ['Industrial Contractors', 'Plumbers', 'Electricians', 'Builders'],
    nearbyAreas: ['Neath', 'Bridgend', 'Swansea', 'Margam'],
    coordinates: { lat: 51.5906, lng: -3.7985 },
  },
  'gorseinon': {
    name: 'Gorseinon',
    intro: 'Located between Swansea and Llanelli, Gorseinon is a growing residential area with strong demand for local trades. The town serves as a hub for surrounding villages and offers excellent opportunities for tradespeople.',
    population: '20,800',
    monthlySearches: '250+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'Roofers'],
    nearbyAreas: ['Llanelli', 'Swansea', 'Pontarddulais', 'Gowerton'],
    coordinates: { lat: 51.6678, lng: -4.0461 },
  },
  'mumbles': {
    name: 'Mumbles',
    intro: 'The affluent seaside village of Mumbles attracts premium trades work. With Victorian properties and modern developments, local tradespeople can command higher rates while serving discerning customers.',
    population: '16,600',
    monthlySearches: '300+',
    trades: ['Kitchen Fitters', 'Bathroom Specialists', 'High-End Builders', 'Decorators'],
    nearbyAreas: ['Swansea', 'Sketty', 'Oystermouth', 'Langland'],
    coordinates: { lat: 51.5726, lng: -3.9966 },
  },
  'ammanford': {
    name: 'Ammanford',
    intro: 'Serving the Amman Valley community, Ammanford tradespeople benefit from strong local loyalty. The town acts as a service hub for surrounding villages, offering steady demand for reliable trades.',
    population: '5,411',
    monthlySearches: '200+',
    trades: ['Plumbers', 'Heating Engineers', 'Builders', 'Roofers'],
    nearbyAreas: ['Llanelli', 'Llandeilo', 'Pontarddulais', 'Cross Hands'],
    coordinates: { lat: 51.7937, lng: -3.9896 },
  },

  // Cardiff & Vale Region
  'cardiff': {
    name: 'Cardiff',
    intro: "The Welsh capital is the most competitive market in Wales. With over 360,000 residents and constant commercial development, only trades with exceptional online presence survive and thrive here. Your website is your most important sales tool.",
    population: '362,310',
    monthlySearches: '4,500+',
    trades: ['All Trades', 'Commercial', 'Specialist Services'],
    nearbyAreas: ['Penarth', 'Barry', 'Caerphilly', 'Newport'],
    coordinates: { lat: 51.4816, lng: -3.1791 },
  },
  'barry': {
    name: 'Barry',
    intro: "Wales' largest town has seen significant regeneration, with Gavin and Stacey putting it on the map. The waterfront development and town centre improvements mean competition among trades is fierce - you need to stand out.",
    population: '54,673',
    monthlySearches: '900+',
    trades: ['All Trades', 'Renovation Specialists'],
    nearbyAreas: ['Cardiff', 'Rhoose', 'Penarth', 'Dinas Powys'],
    coordinates: { lat: 51.3998, lng: -3.2684 },
  },
  'penarth': {
    name: 'Penarth',
    intro: 'An affluent Victorian seaside town, Penarth offers premium opportunities for quality trades. Period properties require specialist skills, and residents expect excellent service and professional presentation.',
    population: '27,226',
    monthlySearches: '400+',
    trades: ['Period Property Specialists', 'Decorators', 'Plumbers', 'Electricians'],
    nearbyAreas: ['Cardiff', 'Barry', 'Dinas Powys', 'Sully'],
    coordinates: { lat: 51.4329, lng: -3.1702 },
  },
  'cowbridge': {
    name: 'Cowbridge',
    intro: "Known as the 'Bond Street of Wales', Cowbridge's affluent market demands premium trades services. Clients here expect quality craftsmanship and professional presentation - your website must reflect that.",
    population: '4,078',
    monthlySearches: '300+',
    trades: ['Kitchen Fitters', 'Landscapers', 'High-End Builders'],
    nearbyAreas: ['Llantwit Major', 'Barry', 'Bridgend'],
    coordinates: { lat: 51.4622, lng: -3.4546 },
  },
  'rhoose': {
    name: 'Rhoose',
    intro: 'Near Cardiff Airport, Rhoose is growing rapidly with new housing developments creating sustained demand for quality trades. Getting established here now means building long-term customer relationships.',
    population: '6,000',
    monthlySearches: '200+',
    trades: ['New Build Specialists', 'Plumbers', 'Electricians'],
    nearbyAreas: ['Barry', 'Llantwit Major', 'Cardiff Airport'],
    coordinates: { lat: 51.3893, lng: -3.3454 },
  },
  'llantwit-major': {
    name: 'Llantwit Major',
    intro: 'A historic Vale of Glamorgan town with a mix of older properties and new developments. Strong community feel means word-of-mouth is powerful, but online presence captures customers searching from further afield.',
    population: '13,366',
    monthlySearches: '250+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'Roofers'],
    nearbyAreas: ['Cowbridge', 'Barry', 'Rhoose', 'Bridgend'],
    coordinates: { lat: 51.4063, lng: -3.4829 },
  },
  'dinas-powys': {
    name: 'Dinas Powys',
    intro: 'An affluent commuter village between Cardiff and Barry, Dinas Powys residents expect quality trades services. The village has a strong community and properties range from period homes to modern developments.',
    population: '10,200',
    monthlySearches: '180+',
    trades: ['Plumbers', 'Electricians', 'Decorators', 'Garden Specialists'],
    nearbyAreas: ['Cardiff', 'Barry', 'Penarth', 'Sully'],
    coordinates: { lat: 51.4368, lng: -3.2145 },
  },

  // Newport & Gwent Region
  'newport': {
    name: 'Newport',
    intro: "Wales' third-largest city is undergoing major development with the Celtic Manor, International Convention Centre, and city centre regeneration. Forward-thinking trades are investing in their digital presence now to capture this growing market.",
    population: '154,676',
    monthlySearches: '1,800+',
    trades: ['All Trades', 'Industrial Contractors'],
    nearbyAreas: ['Cardiff', 'Cwmbran', 'Caerphilly', 'Chepstow'],
    coordinates: { lat: 51.5842, lng: -2.9977 },
  },
  'cwmbran': {
    name: 'Cwmbran',
    intro: "Torfaen's largest town offers steady demand for trades serving residential and commercial clients. The town centre and surrounding estates provide consistent work opportunities for reliable tradespeople.",
    population: '48,535',
    monthlySearches: '550+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'Heating Engineers'],
    nearbyAreas: ['Newport', 'Pontypool', 'Abergavenny', 'Caerphilly'],
    coordinates: { lat: 51.6538, lng: -3.0214 },
  },
  'chepstow': {
    name: 'Chepstow',
    intro: 'A historic border town with a mix of older properties and growing developments. Chepstow serves as a gateway between Wales and England, with trades serving customers on both sides of the border.',
    population: '12,350',
    monthlySearches: '300+',
    trades: ['Plumbers', 'Builders', 'Roofers', 'Period Property Specialists'],
    nearbyAreas: ['Newport', 'Monmouth', 'Caldicot', 'Lydney'],
    coordinates: { lat: 51.6412, lng: -2.6748 },
  },
  'caldicot': {
    name: 'Caldicot',
    intro: 'A growing commuter town in Monmouthshire, Caldicot has seen significant residential development. New housing means demand for trades, while established areas provide renovation and maintenance opportunities.',
    population: '12,300',
    monthlySearches: '200+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'Bathroom Fitters'],
    nearbyAreas: ['Newport', 'Chepstow', 'Magor', 'Rogiet'],
    coordinates: { lat: 51.5874, lng: -2.7574 },
  },
  'abergavenny': {
    name: 'Abergavenny',
    intro: 'Known as the Gateway to Wales, Abergavenny attracts visitors and new residents seeking quality of life. The town has a thriving food scene and growing population, creating demand for skilled trades.',
    population: '14,655',
    monthlySearches: '280+',
    trades: ['Plumbers', 'Builders', 'Kitchen Fitters', 'Decorators'],
    nearbyAreas: ['Cwmbran', 'Monmouth', 'Crickhowell', 'Pontypool'],
    coordinates: { lat: 51.8242, lng: -3.0167 },
  },

  // Valleys Region
  'bridgend': {
    name: 'Bridgend',
    intro: 'Strategically positioned between Cardiff and Swansea, Bridgend trades serve a wide catchment area including the valleys and coast. The Designer Outlet attracts commercial work whilst residential areas provide steady domestic demand.',
    population: '144,876',
    monthlySearches: '1,100+',
    trades: ['All Trades', 'Industrial Contractors'],
    nearbyAreas: ['Porthcawl', 'Maesteg', 'Pencoed', 'Ogmore Vale'],
    coordinates: { lat: 51.5055, lng: -3.5768 },
  },
  'porthcawl': {
    name: 'Porthcawl',
    intro: 'This popular seaside resort has unique requirements with holiday properties and coastal homes needing specialist trades services. Seasonal demand peaks create excellent opportunities for prepared tradespeople.',
    population: '16,005',
    monthlySearches: '400+',
    trades: ['Plumbers', 'Roofers', 'Property Maintenance'],
    nearbyAreas: ['Bridgend', 'Kenfig Hill', 'Nottage'],
    coordinates: { lat: 51.4789, lng: -3.6980 },
  },
  'ogmore-vale': {
    name: 'Ogmore Vale',
    intro: 'Serving the valleys communities, local trades in Ogmore Vale benefit from strong community ties and word-of-mouth recommendations. However, a professional online presence ensures you capture customers searching online too.',
    population: '5,206',
    monthlySearches: '150+',
    trades: ['Plumbers', 'Heating Engineers', 'General Builders'],
    nearbyAreas: ['Bridgend', 'Nantymoel', 'Blackmill'],
    coordinates: { lat: 51.5708, lng: -3.5908 },
  },
  'caerphilly': {
    name: 'Caerphilly',
    intro: 'Home to the famous castle, Caerphilly is a thriving town serving the surrounding valleys. Strong population and proximity to Cardiff create excellent opportunities for trades who stand out online.',
    population: '41,402',
    monthlySearches: '600+',
    trades: ['All Trades', 'Renovation Specialists', 'Heating Engineers'],
    nearbyAreas: ['Cardiff', 'Newport', 'Pontypridd', 'Blackwood'],
    coordinates: { lat: 51.5783, lng: -3.2181 },
  },
  'pontypridd': {
    name: 'Pontypridd',
    intro: 'At the heart of the valleys, Pontypridd serves multiple communities. The town is undergoing regeneration with new developments creating demand, while established areas need maintenance and renovation trades.',
    population: '33,000',
    monthlySearches: '500+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'Roofers'],
    nearbyAreas: ['Cardiff', 'Caerphilly', 'Merthyr Tydfil', 'Aberdare'],
    coordinates: { lat: 51.6018, lng: -3.3423 },
  },
  'merthyr-tydfil': {
    name: 'Merthyr Tydfil',
    intro: "Once the iron capital of the world, Merthyr Tydfil is reinventing itself. Town centre regeneration and new housing create opportunities, while the town's heritage buildings need specialist trades skills.",
    population: '43,820',
    monthlySearches: '550+',
    trades: ['Plumbers', 'Builders', 'Heating Engineers', 'Roofers'],
    nearbyAreas: ['Aberdare', 'Pontypridd', 'Ebbw Vale', 'Tredegar'],
    coordinates: { lat: 51.7475, lng: -3.3780 },
  },
  'aberdare': {
    name: 'Aberdare',
    intro: 'A historic valleys town with a strong community spirit. Aberdare trades serve the town and surrounding villages, with a mix of older terraced properties and modern developments requiring various skills.',
    population: '31,705',
    monthlySearches: '400+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'Heating Engineers'],
    nearbyAreas: ['Merthyr Tydfil', 'Pontypridd', 'Mountain Ash', 'Hirwaun'],
    coordinates: { lat: 51.7149, lng: -3.4496 },
  },
  'maesteg': {
    name: 'Maesteg',
    intro: 'Serving the Llynfi Valley, Maesteg trades benefit from community loyalty and repeat business. The town has a mix of traditional terraced properties and newer developments requiring varied trades skills.',
    population: '18,888',
    monthlySearches: '280+',
    trades: ['Plumbers', 'Electricians', 'Builders', 'General Maintenance'],
    nearbyAreas: ['Bridgend', 'Port Talbot', 'Tonypandy', 'Cymmer'],
    coordinates: { lat: 51.6106, lng: -3.6558 },
  },

  // Pembrokeshire Region
  'tenby': {
    name: 'Tenby',
    intro: 'A premier Welsh holiday destination, Tenby offers unique opportunities for trades serving holiday lets and period properties. Seasonal peaks create intensive demand, while winter brings renovation work.',
    population: '4,696',
    monthlySearches: '350+',
    trades: ['Property Maintenance', 'Plumbers', 'Electricians', 'Decorators'],
    nearbyAreas: ['Pembroke', 'Saundersfoot', 'Kilgetty', 'Narberth'],
    coordinates: { lat: 51.6726, lng: -4.7062 },
  },
  'pembroke-dock': {
    name: 'Pembroke Dock',
    intro: 'A historic dockyard town with a mix of maritime heritage and modern industry. Trades serve residential areas, commercial premises, and the growing renewable energy sector in the waterway.',
    population: '9,753',
    monthlySearches: '250+',
    trades: ['Industrial Contractors', 'Plumbers', 'Electricians', 'Builders'],
    nearbyAreas: ['Pembroke', 'Milford Haven', 'Haverfordwest', 'Tenby'],
    coordinates: { lat: 51.6927, lng: -4.9397 },
  },
  'milford-haven': {
    name: 'Milford Haven',
    intro: "Home to one of the UK's largest energy ports, Milford Haven offers opportunities for trades serving both industrial and residential clients. The waterway development is bringing new investment to the area.",
    population: '12,830',
    monthlySearches: '300+',
    trades: ['Industrial Contractors', 'Plumbers', 'Electricians', 'Marine Specialists'],
    nearbyAreas: ['Haverfordwest', 'Pembroke Dock', 'Neyland', 'Johnston'],
    coordinates: { lat: 51.7129, lng: -5.0397 },
  },
  'haverfordwest': {
    name: 'Haverfordwest',
    intro: 'The county town of Pembrokeshire, Haverfordwest serves as a commercial hub for West Wales. Trades here benefit from a large catchment area including rural communities and coastal towns.',
    population: '13,367',
    monthlySearches: '320+',
    trades: ['All Trades', 'Agricultural Contractors', 'Builders'],
    nearbyAreas: ['Milford Haven', 'Pembroke', 'Fishguard', 'Narberth'],
    coordinates: { lat: 51.8016, lng: -4.9689 },
  },
};

const services = [
  'Custom website design',
  'Local SEO optimisation',
  'Google Business Profile setup',
  'AI-ready schema markup',
  'Content writing',
  'Ongoing maintenance',
];

function generateLocalBusinessSchema(location: LocationInfo, slug: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': 'SmartSite Solutions',
    'description': `Professional web design and local SEO services for tradespeople in ${location.name}, South Wales`,
    'url': `https://smartsite-lead-gen.lovable.app/locations/${slug}`,
    'telephone': '+44 7852 872088',
    'email': 'smartsitesolutions@pm.me',
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': location.coordinates.lat,
      'longitude': location.coordinates.lng,
    },
    'areaServed': [
      {
        '@type': 'City',
        'name': location.name,
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': location.coordinates.lat,
          'longitude': location.coordinates.lng,
        },
      },
      ...location.nearbyAreas.map(area => ({
        '@type': 'Place',
        'name': area,
      })),
    ],
    'serviceArea': {
      '@type': 'GeoCircle',
      'geoMidpoint': {
        '@type': 'GeoCoordinates',
        'latitude': location.coordinates.lat,
        'longitude': location.coordinates.lng,
      },
      'geoRadius': '15',
    },
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': location.name,
      'addressRegion': 'Wales',
      'addressCountry': 'GB',
    },
    'priceRange': '££',
    'openingHoursSpecification': {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      'opens': '09:00',
      'closes': '17:30',
    },
  };
}

export default function Location() {
  const { slug } = useParams<{ slug: string }>();
  const location = slug ? locationData[slug] : null;

  // Add LocalBusiness Schema
  useEffect(() => {
    if (location && slug) {
      // Add LocalBusiness Schema
      const existingSchema = document.querySelector('script[data-schema="local-business"]');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      const schema = document.createElement('script');
      schema.type = 'application/ld+json';
      schema.setAttribute('data-schema', 'local-business');
      schema.textContent = JSON.stringify(generateLocalBusinessSchema(location, slug));
      document.head.appendChild(schema);

      // Cleanup on unmount
      return () => {
        const schemaScript = document.querySelector('script[data-schema="local-business"]');
        if (schemaScript) {
          schemaScript.remove();
        }
      };
    }
  }, [location, slug]);

  // Generate dynamic meta content
  const pageTitle = location ? `Web Design in ${location.name} | SmartSite Solutions` : 'Location | SmartSite Solutions';
  const pageDescription = location 
    ? `Professional websites and local SEO for ${location.trades.slice(0, 3).join(', ').toLowerCase()} in ${location.name}, South Wales. ${location.monthlySearches} monthly searches. Get more leads today.`
    : 'Web design and local SEO for trades in South Wales.';

  if (!location) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-32 pb-24">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-display text-4xl font-bold mb-4">Location Not Found</h1>
            <p className="text-muted-foreground mb-8">We could not find the location you are looking for.</p>
            <Link to="/locations">
              <Button className="gradient-button">View All Locations</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Get nearby areas that have their own pages
  const linkedNearbyAreas = location.nearbyAreas.map(area => {
    const areaSlug = area.toLowerCase().replace(/\s+/g, '-');
    const hasPage = locationData[areaSlug] !== undefined;
    return { name: area, slug: areaSlug, hasPage };
  });

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Locations', href: '/locations' },
    { label: location.name, href: `/locations/${slug}` }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`https://smartsite-lead-gen.lovable.app/locations/${slug}`} />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="w-5 h-5 text-teal-400" />
              <span className="text-teal-400 font-medium">Serving {location.name}</span>
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Websites for Trades in{' '}
              <span className="text-gradient">{location.name}</span>
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8">
              {location.intro}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="gradient-button group">
                  Get Your Free {location.name} Audit
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="ghost" className="glass-button">
                  View Local Case Studies
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid md:grid-cols-3 gap-6 mb-16"
          >
            <div className="glass-card p-6 text-center">
              <p className="text-3xl font-display font-bold text-gradient mb-2">
                {location.population}
              </p>
              <p className="text-sm text-muted-foreground">Population</p>
            </div>
            <div className="glass-card p-6 text-center">
              <p className="text-3xl font-display font-bold text-gradient mb-2">
                {location.monthlySearches}
              </p>
              <p className="text-sm text-muted-foreground">Monthly Trade Searches</p>
            </div>
            <div className="glass-card p-6 text-center">
              <p className="text-3xl font-display font-bold text-gradient mb-2">
                {location.trades.length}+
              </p>
              <p className="text-sm text-muted-foreground">Trade Categories Served</p>
            </div>
          </motion.div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="glass-card p-8">
                <h2 className="font-display text-2xl font-bold mb-6">
                  What We Offer {location.name} Trades
                </h2>
                
                <ul className="space-y-4 mb-8">
                  {services.map((service) => (
                    <li key={service} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-teal-400" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="font-display font-bold mb-4">Trades We Serve in {location.name}</h3>
                <div className="flex flex-wrap gap-2">
                  {location.trades.map((trade) => (
                    <span 
                      key={trade}
                      className="px-4 py-2 rounded-lg bg-white/5 text-sm"
                    >
                      {trade}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="glass-card p-8 h-full flex flex-col justify-center text-center">
                <h3 className="font-display text-2xl font-bold mb-4">
                  Be the First in {location.name}
                </h3>
                <p className="text-muted-foreground mb-6">
                  We are looking for our first showcase client in {location.name}. 
                  Get priority pricing and featured case study status.
                </p>
                <Link to="/contact">
                  <Button className="gradient-button">
                    Claim Your Spot
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Nearby Areas Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-8 mb-16"
          >
            <h2 className="font-display text-2xl font-bold mb-4 text-center">
              Also Serving Nearby Areas
            </h2>
            <p className="text-muted-foreground text-center mb-6">
              We provide web design and SEO services across South Wales, including areas near {location.name}.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {linkedNearbyAreas.map((area) => (
                area.hasPage ? (
                  <Link
                    key={area.name}
                    to={`/locations/${area.slug}`}
                    className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-sm font-medium"
                  >
                    {area.name}
                  </Link>
                ) : (
                  <span
                    key={area.name}
                    className="px-4 py-2 rounded-lg bg-white/5 text-sm text-muted-foreground"
                  >
                    {area.name}
                  </span>
                )
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <div className="glass-card p-8 lg:p-12">
              <h2 className="font-display text-2xl lg:text-3xl font-bold mb-4">
                Ready to Dominate {location.name}?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join the growing number of {location.name} trades who are winning more jobs 
                with professional websites built for local search success.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button className="gradient-button group">
                    Start Your Free Audit
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/locations">
                  <Button variant="ghost" className="glass-button">
                    View All Locations
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
